import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileSpreadsheet, Check, X, AlertCircle, Download, Eye, Play } from 'lucide-react';
import api from '../services/api';
import { uyar, hata, onayla } from '../components/GlobalModal';

interface ImportedEmployee {
  rowNumber: number;
  employeeCode: string | null;
  fullName: string | null;
  department: string | null;
  status: string;
  errorMessage: string | null;
}

interface ImportResult {
  success: boolean;
  totalRows: number;
  importedCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  errors: string[];
  warnings: string[];
  importedEmployees: ImportedEmployee[];
}

interface PreviewData {
  columns: string[];
  data: Record<string, string>[];
  totalRows: number;
}

export default function Import() {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [activeTab, setActiveTab] = useState<'upload' | 'project'>('upload');
  const [projectFileName, setProjectFileName] = useState('Kitap1.xlsx');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreviewData(null);
      setImportResult(null);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && file.name.endsWith('.xlsx')) {
      setSelectedFile(file);
      setPreviewData(null);
      setImportResult(null);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const previewFile = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.uploadFile<Record<string, string>[]>('/import/preview?maxRows=10', formData);

      if (response.success) {
        // Get columns from first row keys
        const data = response.data;
        const columns = data.length > 0 ? Object.keys(data[0]) : [];
        setPreviewData({
          columns,
          data,
          totalRows: data.length
        });
      }
    } catch (error: any) {
      console.error('Preview error:', error);
      uyar(t('import.previewError') + ': ' + (error.response?.data?.message || error.message));
    } finally {
      setIsUploading(false);
    }
  };

  const importFile = async () => {
    if (!selectedFile) return;

    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.uploadFile<ImportResult>('/import/employees/bilteks', formData);

      setImportResult(response.data);
    } catch (error: any) {
      console.error('Import error:', error);
      uyar(t('import.importError') + ': ' + (error.response?.data?.message || error.message));
    } finally {
      setIsImporting(false);
    }
  };

  const previewProjectFile = async () => {
    setIsUploading(true);
    try {
      const response = await api.get<PreviewData>(`/import/preview-project-file?fileName=${projectFileName}&maxRows=10`);

      if (response.success) {
        setPreviewData({
          columns: response.data.columns,
          data: response.data.data,
          totalRows: response.data.totalRows
        });
      }
    } catch (error: any) {
      console.error('Preview error:', error);
      uyar(t('import.previewError') + ': ' + (error.response?.data?.message || error.message));
    } finally {
      setIsUploading(false);
    }
  };

  const importProjectFile = async () => {
    setIsImporting(true);
    try {
      const response = await api.post<ImportResult>(`/import/employees/from-project-file?fileName=${projectFileName}`);
      setImportResult(response.data);
    } catch (error: any) {
      console.error('Import error:', error);
      uyar(t('import.importError') + ': ' + (error.response?.data?.message || error.message));
    } finally {
      setIsImporting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Imported':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"><Check className="w-3 h-3 mr-1" />{t('import.imported')}</span>;
      case 'Updated':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"><Check className="w-3 h-3 mr-1" />{t('import.updated')}</span>;
      case 'Error':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"><X className="w-3 h-3 mr-1" />{t('common.error')}</span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('import.title')}</h1>
        <p className="text-gray-600">{t('import.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => { setActiveTab('upload'); setPreviewData(null); setImportResult(null); }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'upload'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Upload className="w-4 h-4 inline mr-2" />
            {t('import.uploadFile')}
          </button>
          <button
            onClick={() => { setActiveTab('project'); setPreviewData(null); setImportResult(null); }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'project'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileSpreadsheet className="w-4 h-4 inline mr-2" />
            {t('import.projectFile')}
          </button>
        </nav>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Area */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">
            {activeTab === 'upload' ? t('import.selectExcelFile') : t('import.projectFolderFile')}
          </h2>

          {activeTab === 'upload' ? (
            <>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">
                  {selectedFile ? selectedFile.name : t('import.dragOrClick')}
                </p>
                <p className="text-sm text-gray-400">{t('import.onlyXlsx')}</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {selectedFile && (
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={previewFile}
                    disabled={isUploading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                  >
                    <Eye className="w-4 h-4" />
                    {isUploading ? t('common.loading') : t('import.preview')}
                  </button>
                  <button
                    onClick={importFile}
                    disabled={isImporting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Play className="w-4 h-4" />
                    {isImporting ? t('import.processing') : t('import.importAction')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('import.fileName')}</label>
                <input
                  type="text"
                  value={projectFileName}
                  onChange={(e) => setProjectFileName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('import.filenamePlaceholder')}
                />
                <p className="mt-1 text-sm text-gray-500">{t('import.projectFolderDesc')}</p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={previewProjectFile}
                  disabled={isUploading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  <Eye className="w-4 h-4" />
                  {isUploading ? t('common.loading') : t('import.preview')}
                </button>
                <button
                  onClick={importProjectFile}
                  disabled={isImporting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Play className="w-4 h-4" />
                  {isImporting ? t('import.processing') : t('import.importAction')}
                </button>
              </div>
            </>
          )}

          {/* Supported Columns Info */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-700 mb-2">{t('import.supportedColumns')}</h3>
            <div className="text-sm text-gray-600 grid grid-cols-2 gap-1">
              <div>• {t('import.col.employeeNo')}</div>
              <div>• {t('import.col.userNo')}</div>
              <div>• {t('import.col.name')}</div>
              <div>• {t('import.col.department')}</div>
              <div>• {t('import.col.position')}</div>
              <div>• {t('import.col.cardNumber')}</div>
              <div>• {t('import.col.workGroup')}</div>
              <div>• {t('import.col.hireDate')}</div>
              <div>• {t('import.col.status')}</div>
              <div>• {t('import.col.fingerprint')}</div>
            </div>
          </div>
        </div>

        {/* Preview / Result Area */}
        <div className="bg-white rounded-lg shadow p-6">
          {previewData && !importResult && (
            <>
              <h2 className="text-lg font-semibold mb-4">
                <Eye className="w-5 h-5 inline mr-2" />
                {t('import.preview')} ({previewData.totalRows} {t('import.rows')})
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {previewData.columns.map((col, idx) => (
                        <th key={idx} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {previewData.data.map((row, rowIdx) => (
                      <tr key={rowIdx} className="hover:bg-gray-50">
                        {previewData.columns.map((col, colIdx) => (
                          <td key={colIdx} className="px-3 py-2 whitespace-nowrap text-gray-700">
                            {row[col] || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {importResult && (
            <>
              <h2 className="text-lg font-semibold mb-4">
                {importResult.success ? (
                  <Check className="w-5 h-5 inline mr-2 text-green-500" />
                ) : (
                  <AlertCircle className="w-5 h-5 inline mr-2 text-red-500" />
                )}
                {t('import.result')}
              </h2>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-gray-700">{importResult.totalRows}</div>
                  <div className="text-xs text-gray-500">{t('common.total')}</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">{importResult.importedCount}</div>
                  <div className="text-xs text-green-600">{t('import.imported')}</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-600">{importResult.updatedCount}</div>
                  <div className="text-xs text-blue-600">{t('import.updated')}</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-600">{importResult.errorCount}</div>
                  <div className="text-xs text-red-600">{t('import.errors')}</div>
                </div>
              </div>

              {/* Errors */}
              {importResult.errors.length > 0 && (
                <div className="mb-4 p-3 bg-red-50 rounded-lg">
                  <h3 className="font-medium text-red-800 mb-2">{t('import.errors')}</h3>
                  <ul className="text-sm text-red-700 list-disc list-inside">
                    {importResult.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                    {importResult.errors.length > 5 && (
                      <li>... ve {importResult.errors.length - 5} hata daha</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Imported Employees Table */}
              <div className="overflow-x-auto max-h-64">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('import.row')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('import.col.employeeNo')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('import.col.name')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('import.col.department')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('common.status')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {importResult.importedEmployees.slice(0, 50).map((emp, idx) => (
                      <tr key={idx} className={emp.status === 'Error' ? 'bg-red-50' : 'hover:bg-gray-50'}>
                        <td className="px-3 py-2 text-gray-500">{emp.rowNumber}</td>
                        <td className="px-3 py-2 text-gray-700">{emp.employeeCode || '-'}</td>
                        <td className="px-3 py-2 text-gray-700">{emp.fullName || '-'}</td>
                        <td className="px-3 py-2 text-gray-700">{emp.department || '-'}</td>
                        <td className="px-3 py-2">
                          {getStatusBadge(emp.status)}
                          {emp.errorMessage && (
                            <span className="block text-xs text-red-600 mt-1">{emp.errorMessage}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!previewData && !importResult && (
            <div className="h-full flex items-center justify-center text-gray-400">
              <div className="text-center">
                <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>{t('import.resultPlaceholder')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
